import { ChangeEvent, useState } from "react";

import { Formik } from "formik";

import { useAddLicenceMutation, useEditLicenceMutation } from "@sso/apis/licences";
import { useSearchOrganisationsMutation } from "@sso/apis/organisations";
import { useSoftwaresQuery } from "@sso/apis/softwares";
import { DialogActionButtons, DialogBody, DialogHeader } from "@sso/components/Dialogs/components";
import { SelectDataType } from "@sso/components/Form/Select";
import { FormikCheckbox, FormikTextInput } from "@sso/components/Formik";
import FormikSelectWithBlur from "@sso/components/Formik/FormikSelectWtihBlur";
import FormikTypeahead from "@sso/components/Formik/FormikTypeahead";
import { DialogTitle, UserRole } from "@sso/constant";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import {
  Licence,
  LicenceFormType,
  LicenceKeys,
  LicenceType,
  SoftwareKeys,
  SoftwareLicenceKeys,
  SoftwareLicenceSummaryKeys,
  TimeUnit,
  licenceTypeOption,
} from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import { licenceSchemeValidation } from "./schema";
import UserByUserOrgSelect from "./userByUserOrgSelect";

interface LicenceFormProps {
  licence?: Licence;
}

const LicenceForm = ({ licence }: LicenceFormProps) => {
  const [addLicence, { isLoading: addLoading }] = useAddLicenceMutation();
  const [editLicence, { isLoading: editLoading }] = useEditLicenceMutation();
  const { data: softwares, isSuccess } = useSoftwaresQuery();
  const softwareOptions = softwares
    ? softwares.map(s => ({ key: s.softwareId, value: s.softwareId, label: s.name }))
    : [];

  const checkForTraccLicence = (softwareLicenceId: string | undefined): boolean => {
    return (
      softwareLicenceId?.toUpperCase() === process.env.REACT_APP_TRACC_SINGLE_ID?.toUpperCase() ||
      softwareLicenceId?.toUpperCase() === process.env.REACT_APP_TRACC_CONCURRENT_ID?.toUpperCase()
    );
  };

  const checkForTraccConcurrentLicence = (softwareLicenceId: string | undefined): boolean => {
    return softwareLicenceId?.toUpperCase() === process.env.REACT_APP_TRACC_CONCURRENT_ID?.toUpperCase();
  };

  const handleSubmit = (values: LicenceFormType) => {
    const isTraccLicence = checkForTraccLicence(values[LicenceKeys.SOFTWARE_LICENCE_ID]);

    let licenceValues;

    /// Can be improved on for other additional data requirements in the future
    if (isTraccLicence) {
      const baseValues =
        values.type === LicenceType.SINGLE_USER || values.type === LicenceType.TRIAL
          ? { ...values, concurrentUsers: undefined }
          : { ...values, userId: undefined };

      const additionalData = {
        HardwareId: values.Tracc_HardwareId,
        ServerAddress: values.Tracc_ServerAddress,
        Description: values.Tracc_Description,
        type: "Tracc",
      };

      const { Tracc_HardwareId, Tracc_ServerAddress, Tracc_Description, ...cleanValues } = baseValues;

      licenceValues = {
        ...cleanValues,
        additionalData: additionalData,
      };
    } else {
      // For non-Tracc licences, use the existing logic
      licenceValues =
        values.type === LicenceType.SINGLE_USER || values.type === LicenceType.TRIAL
          ? { ...values, concurrentUsers: undefined }
          : { ...values, userId: undefined };
    }

    licence ? editLicence(licenceValues) : addLicence(licenceValues);
  };

  const [organisationOptions, setOrganisationOptions] = useState<SelectDataType[]>([]);
  const [searchOrganisations] = useSearchOrganisationsMutation();

  const handleOrganisationSearch = (searchTerm: string) => {
    if (!searchTerm) {
      setOrganisationOptions([]);
      return;
    }
    searchOrganisations(searchTerm).then(response => {
      if ("data" in response) {
        const organisations = response.data;
        const options = organisations.map(org => ({
          key: org.organisationId,
          value: org.organisationId,
          label: org.name,
        }));
        setOrganisationOptions(options);
      }
    });
  };

  const getSoftwareLicenceOptions = (softwareId: string | undefined) => {
    if (!softwareId || !softwares) {
      return [];
    }
    const software = softwares.find(f => f[SoftwareKeys.SOFTWARE_ID] === softwareId);
    if (!software) {
      return [];
    }
    return software[SoftwareKeys.SOFTWARE_LICENCES].map(sl => ({
      key: sl[SoftwareLicenceKeys.SOFTWARE_LICENCE_ID],
      value: sl[SoftwareLicenceKeys.SOFTWARE_LICENCE_ID],
      label: sl[SoftwareLicenceKeys.NAME],
    }));
  };

  const handleOrganisationClear = () => {
    setOrganisationOptions([]);
  };

  const addExpiry = (expiryValue?: number, expiryUnit?: TimeUnit, date?: Date): string | undefined => {
    if (!date) {
      return;
    }

    if (!expiryValue || !expiryUnit) {
      return;
    }

    const newDate = new Date(date);

    switch (expiryUnit.toUpperCase()) {
      case TimeUnit.Day:
        newDate.setDate(newDate.getDate() + expiryValue);
        break;
      case TimeUnit.Month:
        newDate.setMonth(newDate.getMonth() + expiryValue);
        break;
      case TimeUnit.Year:
        newDate.setFullYear(newDate.getFullYear() + expiryValue);
        break;
    }

    return newDate.toISOString().split("T")[0];
  };

  const selectedSoftwareLicence = (values: LicenceFormType, softwareLicenceId: string) => {
    const softwareId = values[SoftwareKeys.SOFTWARE_ID];

    if (!softwareId || !softwareLicenceId || !softwares) {
      return;
    }

    const software = softwares.find(f => f[SoftwareKeys.SOFTWARE_ID] === softwareId);

    if (!software) {
      return;
    }

    return software[SoftwareKeys.SOFTWARE_LICENCES].find(
      sl => sl[SoftwareLicenceKeys.SOFTWARE_LICENCE_ID] === softwareLicenceId,
    );
  };

  const extractTraccData = (licence?: Licence) => {
    if (!licence?.additionalData) {
      return {
        Tracc_HardwareId: "",
        Tracc_ServerAddress: "",
        Tracc_Description: "",
      };
    }

    try {
      const additionalData =
        typeof licence.additionalData === "string" ? JSON.parse(licence.additionalData) : licence.additionalData;
      return {
        Tracc_HardwareId: additionalData?.hardwareId || "",
        Tracc_ServerAddress: additionalData?.serverAddress || "",
        Tracc_Description: additionalData?.description || "",
      };
    } catch (error) {
      console.error("Error parsing additionalData:", error);
      return {
        Tracc_HardwareId: "",
        Tracc_ServerAddress: "",
        Tracc_Description: "",
      };
    }
  };

  const initialUpdateValue = {
    [LicenceKeys.LICENCE_ID]: licence?.licenceId,
    [LicenceKeys.SOFTWARE_ID]: licence?.softwareLicence.software.id,
    [LicenceKeys.SOFTWARE_LICENCE_ID]: licence?.softwareLicence.softwareLicenceId,
    [LicenceKeys.CONCURRENT_USERS]: licence?.concurrentUsers ?? undefined,
    [LicenceKeys.EXPIRY_DATE]: licence?.expiryDate ? licence.expiryDate!.split("T")[0] : undefined,
    [LicenceKeys.ORGANISATION_ID]: licence?.organisation.organisationId,
    [LicenceKeys.TYPE]: licence?.type,
    [LicenceKeys.USER_ID]: licence?.user?.userId ?? undefined,
    [LicenceKeys.NEVER_EXPIRE]: licence?.expiryDate ? false : true,
    // Extract Tracc data for editing
    ...extractTraccData(licence),
  } as LicenceFormType;
  const user = useAppSelector(selectCurrentUser);
  const isAdmin = user?.role.roleId === UserRole.ADMINISTRATOR;

  return (
    <>
      {softwares && isSuccess && (
        <>
          <DialogHeader title={licence ? DialogTitle.EDIT_LICENCE : DialogTitle.ADD_LICENCE} />

          <Formik onSubmit={handleSubmit} initialValues={initialUpdateValue} validationSchema={licenceSchemeValidation}>
            {({ values, setFieldValue }) => (
              <CustomForm width="100%" overflow="hidden">
                <DialogBody>
                  <FormikTypeahead
                    name={LicenceKeys.ORGANISATION_ID}
                    label="Search Organisations..."
                    items={organisationOptions}
                    onSearch={handleOrganisationSearch}
                    clear={handleOrganisationClear}
                    value={
                      licence
                        ? {
                            label: licence.organisation.name,
                            value: licence.organisation.organisationId,
                            key: licence.organisation.organisationId,
                          }
                        : undefined
                    }
                  />

                  <FormikSelectWithBlur name={LicenceKeys.SOFTWARE_ID} label="Product" items={softwareOptions} />

                  {values[LicenceKeys.SOFTWARE_ID] && (
                    <>
                      <FormikSelectWithBlur
                        name={LicenceKeys.SOFTWARE_LICENCE_ID}
                        label="Type"
                        items={getSoftwareLicenceOptions(values[LicenceKeys.SOFTWARE_ID])}
                        onChange={value => {
                          const sl = selectedSoftwareLicence(values, value.toString());
                          if (!sl) {
                            return;
                          }
                          const licenceType = sl[SoftwareLicenceSummaryKeys.LICENCE_TYPE];
                          const expiryUnit = sl[SoftwareLicenceSummaryKeys.EXPIRY_UNIT];
                          const expiryValue = sl[SoftwareLicenceSummaryKeys.EXPIRY_VALUE];

                          setFieldValue(
                            LicenceKeys.EXPIRY_DATE,
                            expiryValue ? addExpiry(expiryValue, expiryUnit, new Date()) : undefined,
                          );
                          setFieldValue(LicenceKeys.NEVER_EXPIRE, expiryUnit == null || expiryValue == null);
                          setFieldValue(LicenceKeys.TYPE, licenceType);
                          if (licenceType === LicenceType.CONCURRENT) {
                            setFieldValue(LicenceKeys.CONCURRENT_USERS, sl.concurrentUsers);
                          }
                        }}
                      />
                    </>
                  )}

                  {values[LicenceKeys.SOFTWARE_LICENCE_ID] && (
                    <FormikCheckbox
                      name={LicenceKeys.NEVER_EXPIRE}
                      label="Never expire"
                      onChange={(value: any) => {
                        const checked = value.target.checked;
                        setFieldValue(LicenceKeys.NEVER_EXPIRE, checked);
                        if (checked) {
                          setFieldValue(LicenceKeys.EXPIRY_DATE, undefined);
                        } else {
                          const sl = selectedSoftwareLicence(values, values[LicenceKeys.SOFTWARE_LICENCE_ID]!);
                          if (!sl) {
                            return;
                          }
                          const expiryUnit = sl[SoftwareLicenceSummaryKeys.EXPIRY_UNIT];
                          const expiryValue = sl[SoftwareLicenceSummaryKeys.EXPIRY_VALUE];

                          setFieldValue(LicenceKeys.EXPIRY_DATE, addExpiry(expiryValue, expiryUnit, new Date()));
                        }
                      }}
                    />
                  )}

                  <div style={{ marginTop: "1rem" }}></div>
                  {values[LicenceKeys.SOFTWARE_LICENCE_ID] &&
                    (values[LicenceKeys.NEVER_EXPIRE] == null || values[LicenceKeys.NEVER_EXPIRE] === false) && (
                      <FormikTextInput type="date" name={LicenceKeys.EXPIRY_DATE} label="Expiry date" />
                    )}

                  {values[LicenceKeys.SOFTWARE_LICENCE_ID] &&
                    (values[LicenceKeys.NEVER_EXPIRE] ||
                      (!values[LicenceKeys.NEVER_EXPIRE] && values[LicenceKeys.EXPIRY_DATE])) && (
                      <>
                        <FormikSelectWithBlur disabled name={LicenceKeys.TYPE} label="Type" items={licenceTypeOption} />
                        {(values[LicenceKeys.TYPE] === LicenceType.SINGLE_USER ||
                          values[LicenceKeys.TYPE] === LicenceType.TRIAL) &&
                          values[LicenceKeys.ORGANISATION_ID] !== undefined && (
                            <UserByUserOrgSelect organisationId={values[LicenceKeys.ORGANISATION_ID]} />
                          )}
                        {values[LicenceKeys.TYPE] === LicenceType.CONCURRENT && (
                          <FormikTextInput
                            disabled={!isAdmin}
                            type="number"
                            name={LicenceKeys.CONCURRENT_USERS}
                            label="Concurrent Count"
                          />
                        )}
                      </>
                    )}

                  {values[LicenceKeys.SOFTWARE_LICENCE_ID] &&
                    checkForTraccLicence(values[LicenceKeys.SOFTWARE_LICENCE_ID]) && (
                      <>
                        <p>
                          <strong>Tracc Licence Additional Information:</strong>
                        </p>
                        <br />
                        <FormikTextInput type="text" name="Tracc_HardwareId" label="Hardware Id" />
                        {checkForTraccConcurrentLicence(values[LicenceKeys.SOFTWARE_LICENCE_ID]) && (
                          <FormikTextInput type="text" name="Tracc_ServerAddress" label="Server Address" />
                        )}
                        <FormikTextInput type="text" name="Tracc_Description" label="Licence Description" />
                      </>
                    )}
                </DialogBody>
                <DialogActionButtons loading={addLoading || editLoading} buttonTitle={licence ? "Edit" : "Add"} />
              </CustomForm>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default LicenceForm;
